import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getApiBase, withBase } from '../config/api';
import '../styles/admin.css';

const Message = ({ text, type }) => {
  if (!text) return null;
  return <div className={`alert alert-${type} mt-2`}>{text}</div>;
};

// --- Upload Form Component ---
const TestimonialUploadForm = ({ onUploadSuccess }) => {
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title) {
      setError('Title (e.g., Partner Name) is required.');
      return;
    }
    if (!file) {
      setError('Please select a video file to upload.');
      return;
    }
    if (file.size > 50 * 1024 * 1024) { // 50MB limit
      setError('File is too large. Max 50MB.');
      return;
    }

    const formData = new FormData();
    formData.append('title', title);
    formData.append('video', file); // "video" must match upload.single("video")

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await axios.post(`${getApiBase()}/api/testimonials`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setTitle('');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = null;
      setLoading(false);
      setSuccess('Testimonial uploaded successfully!');
      onUploadSuccess(); // Refresh the list
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload video.');
      setLoading(false);
    }
  };

  return (
    <div className="admin-card mb-4">
      <div className="card-body">
        <h5 className="mb-3">Upload New Testimonial</h5>
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label htmlFor="testimonialTitle" className="form-label">Title / Partner Name</label>
            <input
              type="text"
              className="form-control"
              id="testimonialTitle"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. John Doe - ABC Textiles"
            />
          </div>
          <div className="mb-3">
            <label htmlFor="testimonialVideo" className="form-label">Video File</label>
            <input
              type="file"
              className="form-control"
              id="testimonialVideo"
              ref={fileInputRef}
              accept="video/mp4,video/quicktime"
              onChange={handleFileChange}
            />
            <small className="text-muted">Max file size: 50MB. MP4 format recommended.</small>
          </div>
          <button type="submit" className="btn btn-admin-primary" disabled={loading}>
            {loading ? 'Uploading...' : 'Upload Testimonial'}
          </button>
        </form>
        {error && <Message text={error} type="danger" />}
        {success && <Message text={success} type="success" />}
      </div>
    </div>
  );
};

// --- Main Page Component ---
const ManageTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${getApiBase()}/api/testimonials`);
      setTestimonials(res.data);
      setError('');
    } catch (err) {
      setError('Failed to load testimonials.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this testimonial? This will also delete the video file.')) {
      try {
        await axios.delete(`${getApiBase()}/api/testimonials/${id}`);
        fetchData(); // Refresh the list
      } catch (err) {
        alert('Failed to delete testimonial.');
      }
    }
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <h1 className="display-4 fw-bold mb-3 admin-dashboard-title">Manage Testimonials</h1>
      
      <TestimonialUploadForm onUploadSuccess={fetchData} />
      
      {error && <Message text={error} type="danger" />}
      
      <h3 className="mb-3" style={{ borderBottom: '2px solid #eee', paddingBottom: '10px' }}>
        Existing Testimonials
      </h3>
      {testimonials.length === 0 ? (
        <p className="text-muted">No testimonials uploaded yet.</p>
      ) : (
        <div className="row g-3">
          {testimonials.map((video) => (
            <div key={video.id} className="col-md-6 col-lg-4">
              <div className="admin-card">
                <video
                  controls
                  muted
                  src={withBase(video.video_src)}
                  style={{ width: '100%', height: '200px', objectFit: 'cover', borderRadius: '8px 8px 0 0' }}
                />
                <div className="card-body p-3">
                  <h6 className="mb-3" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {video.title}
                  </h6>
                  <button
                    className="btn btn-sm btn-outline-danger w-100"
                    onClick={() => handleDelete(video.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ManageTestimonials;